import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import {
  ANALYTICS_LIMITS,
  type AnalyticsFilter,
  type AnalyticsQuery,
  type SortDirection,
} from '@/shared/api';
import { formatFilter, normalizeFilter, parseFilter, validateFilter } from './filters';
import { findColumn, type AnalyticsObject, timeSortColumn } from './schema';
import { ALL_VIEW_ID, type TableView } from './tableViews';

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

const PARAM = {
  page: 'page',
  pageSize: 'pageSize',
  sortBy: 'sortBy',
  sortDirection: 'sortDirection',
  filter: 'filter',
} as const;

function positiveInt(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

/** Reads list state from the URL, dropping anything the API would reject. */
export function readAnalyticsQuery(table: AnalyticsObject, search: URLSearchParams) {
  const filters: AnalyticsFilter[] = [];
  let ignoredFilters = 0;
  for (const raw of search.getAll(PARAM.filter)) {
    const parsed = parseFilter(raw);
    if (parsed && filters.length < ANALYTICS_LIMITS.maxFilters && !validateFilter(table, parsed)) {
      filters.push(normalizeFilter(table, parsed));
    } else {
      ignoredFilters += 1;
    }
  }

  const sortColumn = findColumn(table, search.get(PARAM.sortBy));
  const newest = sortColumn ? undefined : timeSortColumn(table);
  const query: AnalyticsQuery = {
    pageNumber: positiveInt(search.get(PARAM.page), 1),
    pageSize: Math.min(
      positiveInt(search.get(PARAM.pageSize), DEFAULT_PAGE_SIZE),
      ANALYTICS_LIMITS.maxPageSize,
    ),
    sortDirection: newest || search.get(PARAM.sortDirection) === 'desc' ? 'desc' : 'asc',
    filters,
    ...(sortColumn ? { sortBy: sortColumn.name } : newest ? { sortBy: newest } : {}),
  };
  return { query, ignoredFilters };
}

/** Name and value of the parameter that opens a table page on its records (Data tab). */
export const TAB_PARAM = 'tab';
export const DATA_TAB = 'data';

/** Builds a records search string (Data tab), e.g. for "open in full view" links. */
export function buildAnalyticsSearch(filters: AnalyticsFilter[]): string {
  const search = new URLSearchParams({ [TAB_PARAM]: DATA_TAB });
  for (const filter of filters) search.append(PARAM.filter, formatFilter(filter));
  const text = search.toString();
  return text ? `?${text}` : '';
}

/** Name of the parameter that marks the active table view (`?view=paid`). */
export const VIEW_PARAM = 'view';

/**
 * The records (Data tab) in the view's filters and sort, keeping the current page size:
 * `?tab=data&view=paid&filter=…&sortBy=…`.
 */
export function viewSearch(
  view: Pick<TableView, 'id' | 'filters' | 'sortBy' | 'sortDirection'>,
  current?: URLSearchParams,
): string {
  const search = new URLSearchParams({ [TAB_PARAM]: DATA_TAB });
  if (view.id !== ALL_VIEW_ID) search.set(VIEW_PARAM, view.id);
  for (const filter of view.filters) search.append(PARAM.filter, formatFilter(filter));
  if (view.sortBy) {
    search.set(PARAM.sortBy, view.sortBy);
    search.set(PARAM.sortDirection, view.sortDirection ?? 'asc');
  }
  const pageSize = current?.get(PARAM.pageSize);
  if (pageSize) search.set(PARAM.pageSize, pageSize);
  const text = search.toString();
  return text ? `?${text}` : '';
}

/**
 * List state (page, page size, sort, filters) kept in the URL so views are shareable,
 * bookmarkable and work with back/forward. Uses the API's own `filter=Column:op:value` format.
 */
export function useAnalyticsSearchParams(table: AnalyticsObject) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { query, ignoredFilters } = useMemo(
    () => readAnalyticsQuery(table, searchParams),
    [table, searchParams],
  );

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void, { resetPage = true } = {}) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        mutate(next);
        if (resetPage) next.delete(PARAM.page);
        return next;
      });
    },
    [setSearchParams],
  );

  const writeFilters = useCallback((next: URLSearchParams, filters: AnalyticsFilter[]) => {
    next.delete(PARAM.filter);
    for (const filter of filters) next.append(PARAM.filter, formatFilter(filter));
  }, []);

  return {
    query,
    ignoredFilters,
    setPage: useCallback(
      (page: number) => {
        update(
          (next) => {
            if (page <= 1) next.delete(PARAM.page);
            else next.set(PARAM.page, String(page));
          },
          { resetPage: false },
        );
      },
      [update],
    ),
    setPageSize: useCallback(
      (pageSize: number) => {
        update((next) => {
          next.set(PARAM.pageSize, String(pageSize));
        });
      },
      [update],
    ),
    /** Cycles a column through ascending → descending → default order. */
    toggleSort: useCallback(
      (column: string) => {
        update((next) => {
          const isCurrent = query.sortBy === column;
          if (isCurrent && query.sortDirection === 'desc') {
            // The newest-first default is not in the URL: the next click sorts oldest first.
            if (!next.has(PARAM.sortBy)) {
              next.set(PARAM.sortBy, column);
              next.set(PARAM.sortDirection, 'asc');
              return;
            }
            next.delete(PARAM.sortBy);
            next.delete(PARAM.sortDirection);
            return;
          }
          const direction: SortDirection = isCurrent ? 'desc' : 'asc';
          next.set(PARAM.sortBy, column);
          next.set(PARAM.sortDirection, direction);
        });
      },
      [update, query.sortBy, query.sortDirection],
    ),
    setFilters: useCallback(
      (filters: AnalyticsFilter[]) => {
        update((next) => {
          writeFilters(next, filters);
        });
      },
      [update, writeFilters],
    ),
  };
}
